import { useEffect, useState } from "react";
import "../CourseList/CourseList.scss";
import { Autocomplete, CircularProgress, ListItem, ListItemText, TextField } from "@mui/material";
import TimeIcon from "../../assets/images/vactor/sm-time.svg";
import CalendarIcon from "../../assets/images/vactor/sm-calender.svg";
import { PrimaryButton } from "../AllButtons/AllButtons";
import {useAppDispatch, useAppSelector } from "../../Store/store";
import { useNavigate } from "react-router-dom";
import { getCourseList, getTaskStatus } from "../../Services/course.service";
import { reviewAnswer } from "../../Slice/courseSlice";
import { useCoursePolling } from "../../hooks/useCoursePolling";
import Loader from "../Loader/Loader/Loader";
import server from "../../middleware/interceptor";

const selectRelevant = [
  { label: "Relevant1 " },
  { label: "Relevant2 " },
  { label: "Relevant3" },
];
export interface Course {
  course_id: number;
  course_level: 'beginner' | 'intermediate' | 'advanced';
  course_name: string;
  created_at: string;
  progress_status: 'not_started' | 'in_progress' | 'completed';
  lesson_status?: 'not_started' | 'in_progress' | 'completed';
  generation_status: string;
  generation_message: string;
  task_id: string;
  is_latest: boolean;
  is_started: boolean;
  estimated_duration_minutes: number;
  estimated_duration_label: string;
  estimated_time_left_minutes: number;
  estimated_time_left_label: string;
  estimated_weeks_label: string;
  estimated_hours_per_week_label: string;
  total_estimated_time_label: string;
}

export interface CourseListResponse {
  success: boolean;
  user_id: string;
  total: number;
  courses: Course[];
}

const CourseList = () => {
  const {  
    startPolling,
    stopPolling,  
  } = useCoursePolling();
  const courses_status = useAppSelector(state => state.course?.status);
  const courseList: CourseListResponse = useAppSelector(state => state.course?.courseList);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [courseStatuses, setCourseStatuses] = useState<Record<string, 'not_started' | 'in_progress' | 'completed'>>({});
  const [statusesLoading, setStatusesLoading] = useState(true);
  const showNoCoursesMessage = courseList?.courses?.length === 0 && courses_status !== "PENDING";
  const isLoading = (courses_status === "PENDING" || courses_status === "pending" || !courseList) || statusesLoading;

  useEffect(() => {
    dispatch(getCourseList(localStorage.getItem("id")));
    const taskId = localStorage.getItem('taskId');
    const advancetaskid = localStorage.getItem('advancetaskid');
    const intermediatetaskid = localStorage.getItem('intermediatetaskid');
    if (taskId) {
      startPolling(String(taskId));
    }

    if (advancetaskid) {
      startPolling(String(advancetaskid));
    }

    if (intermediatetaskid) {
      startPolling(String(intermediatetaskid));
    }
    return () => {
      stopPolling(); 
    };
  }, []);

  useEffect(() => {
    const getCourses=async()=>{
      if(courses_status!=="PENDING" && courses_status!=="pending"){
        await dispatch(getCourseList(localStorage.getItem("id")));
      }
    }    
    getCourses();
  }, [courses_status]);

  useEffect(() => {
    const fetchCourseStatuses = async () => {
      if (!courseList?.courses?.length) {
        setStatusesLoading(false);
        return;
      }
      setStatusesLoading(true);
      try {
        const statusEntries = await Promise.all(
          courseList.courses.map(async (course) => {
            try {
              const response = await server.get(`/course/task-status/${course.task_id}`);
              const taskCourse = response?.data?.result?.course;
              const subLessons = taskCourse?.course_content?.[0]?.sub_lessons || [];
              const lessonStatuses = subLessons
                .map((lesson:any) => lesson?.lesson_status)
                .filter(Boolean);

              let derivedStatus: 'not_started' | 'in_progress' | 'completed' =
                course.lesson_status || course.progress_status || 'not_started';

              if (lessonStatuses.length > 0) {
                if (lessonStatuses.every((status:string) => status === 'completed')) {
                  derivedStatus = 'completed';
                } else if (lessonStatuses.some((status:string) => status === 'completed' || status === 'in_progress')) {
                  derivedStatus = 'in_progress';
                } else {
                  derivedStatus = 'not_started';
                }
              }

              const courseStarted = 
                response?.data?.course_started === true || 
                response?.data?.result?.course_started === true ||
                taskCourse?.course_started === true;

              if (derivedStatus === 'not_started' && courseStarted) {
                derivedStatus = 'in_progress';
              }

              return [course.task_id, derivedStatus] as const;
            } catch (error) {
              return [course.task_id, course.lesson_status || course.progress_status || 'not_started'] as const;
            }
          })
        );

        setCourseStatuses(Object.fromEntries(statusEntries));
      } catch (error) {
        console.error("Error fetching course statuses:", error);
      } finally {
        setStatusesLoading(false);
      }
    };

    fetchCourseStatuses();
  }, [courseList]);

  const enrollCourse=(taskId:any,course_level:string)=>{
    const navPath = {
      beginner: '/dashboard/enrolled_course',
      intermediate: '/dashboard/intermediate_course',
      advanced: '/dashboard/advanced_course'
    }[course_level] || '/dashboard/enrolled_course';

    if(course_level==='beginner'){
      localStorage.setItem('taskId', taskId);      
    }else if(course_level==='intermediate'){
      localStorage.setItem('intermediatetaskid', taskId);
    }else if(course_level==='advanced'){
      localStorage.setItem('advancetaskid', taskId);
    }
    dispatch(reviewAnswer(false));
    dispatch(getTaskStatus(taskId));
    navigate(navPath);
  }
  return (
    <>    
      <div className="course-list-wrapper"> 
        <div className="course-list-head-content">
          <h4>{showNoCoursesMessage ? "No Recommended Courses Yet" : "Recommended Courses for You"}</h4>
          <p>
            {showNoCoursesMessage
              ? <>You haven&rsquo;t taken any diagnostic test yet. Complete a diagnostic test to get personalized course recommendations based on your skills and performance.</>
              : "Based on your diagnostic results, these courses are tailored to boost your skills!"}
          </p>
          <div className="relevant-field-block d-none">
            <div className="input-form-field-wrapper">
              <Autocomplete
                className="Relevant"
                id="Relevant"
                options={selectRelevant}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className="out-label-input-text"
                    placeholder="Relevant"
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    name="Relevant"
                  />
                )}
                renderOption={(props, option) => (
                  <ListItem className="autocomplete-list-items" {...props}>
                    <ListItemText
                      sx={{ padding: "0px", margin: "0px" }}
                      primary={option.label}
                    />
                  </ListItem>
                )}
              />
            </div>
          </div>
        </div>

        <div className="course-list-body" style={{ position: "relative", minHeight: isLoading ? "200px" : "auto" }}>
          {isLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "rgba(255, 255, 255, 0.35)",
                backdropFilter: "blur(2px)",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress size={52} sx={{ color: "#4F46E5" }} />
            </div>
          )}
          <div
            style={{
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.7 : 1,
              transition: "opacity 0.2s ease",
              width: "100%",
              height: "100%"
            }}
          >
            <div className="row gy-4">
              {courses_status==="PENDING" && (<>
                <div className="col-sm-6 col-md-6 col-lg-4 text-center">
                    <div className="course-list-card">
                      <div className="course-list-card-body recommended-courses-loading">                  
                          <Loader/>
                          Generating your course...                                
                      </div>               
                    </div>
                </div>
              </>)}
              { courseList?.courses?.map((course, index) => {        
                let badgeClass = '';
                if (course.course_level === 'beginner') badgeClass = 'beginner-badge';
                else if (course.course_level === 'intermediate') badgeClass = 'intermediate-badge';
                else if (course.course_level === 'advanced') badgeClass = 'advance-badge';

                const courseStatus = courseStatuses[course.task_id] || course.lesson_status || course.progress_status;
              
                const duration:any = {
                  beginner: { weeks: 4, hours: 2 },
                  intermediate: { weeks: 3, hours: 4 },
                  advanced: { weeks: 8, hours: 4 }
                }[course.course_level] || { weeks: 4, hours: 2 };
              
                // const [title, description] = course?.course_name.split(":");
                let title = course.course_name;
                let description = "";

                  if (course.course_name.includes(":")) {
                    const parts = course.course_name.split(":");
                    title = parts[0];
                    description = parts[1]?.trim() || "";
                  } else {
                    const topic = course.course_name.replace(/.*on /, "");
                    if (course.course_level === 'beginner') {
                      description = `Master the basics of ${topic} with interactive exercises.`;
                    } else if (course.course_level === 'intermediate') {
                      description = `Learn ${topic} through practical problems and examples.`;
                    } else if (course.course_level === 'advanced') {
                      description = `Deep dive into ${topic} with expert-level guidance.`;
                    }
                  }
                  return (
                    <div className="col-sm-6 col-md-6 col-lg-4" key={course.course_id}>
                      <div className="course-list-card" style={{ position: "relative" }}>
                        {!courseStatuses[course.task_id] && (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              background: "rgba(255, 255, 255, 0.35)",
                              backdropFilter: "blur(2px)",
                              zIndex: 10,
                              borderRadius: "inherit",
                            }}
                          >
                            <CircularProgress size={52} sx={{ color: "#4F46E5" }} />
                          </div>
                        )}
                        <div className="course-list-card-body">
                          <div className={`course-list-badge ${badgeClass}`}>
                            {course.course_level.charAt(0).toUpperCase() + course.course_level.slice(1)}
                          </div>
                          <h4 className="card-title">{title}</h4>
                          <p className="card-subtitle">{description}</p>
                          {/* <div className="course-duration-block">
                          <h6 className="mb-3">Estimate time</h6>
                          <div className="course-weeks">
                         <img src={TimeIcon} alt="" /> {course.estimated_weeks_label || `${duration.weeks} weeks`}
                          </div>
                          <div className="course-hrs-weeks">
                            <img src={CalendarIcon} alt="" /> {course.estimated_hours_per_week_label || `${duration.hours} hrs/week`}
                          </div>
                        </div> */}
                        </div>
                        <div className="course-list-card-footer">
                          <div className="course-duration-block">
                            <h6 className="mb-3">Estimate time</h6>
                            <div className="course-weeks">
                              <img src={TimeIcon} alt="" /> {course.total_estimated_time_label || `${duration.weeks} weeks`}
                            </div>
                            {/* <div className="course-hrs-weeks">
                              <img src={CalendarIcon} alt="" /> {course.estimated_hours_per_week_label || `${duration.hours} hrs/week`}
                            </div> */}
                          </div>
                          <PrimaryButton onClick={() => enrollCourse(course?.task_id, course?.course_level)} >
                            {courseStatus === 'completed'
                              ? 'Completed'
                              : courseStatus === 'not_started'
                                ? 'Start Course'
                                : 'Continue Course'}
                          </PrimaryButton>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseList;
